// Zugzusammenstellung und Waggontypen.
//
// Vorgabe: 1 Zug, 10 Waggons. Manche mit Kohle gefüllt, manche ganz zu, manche
// mit Innenraum, in den man rein kann.
//
// Konzept §6.2: Waggons müssen individuell wiedererkennbar sein (Nummern,
// Beschädigungen) — ein Waggon, den man zum dritten Mal sieht, schlägt tausend
// anonyme. Deshalb hat jeder Waggon eine feste, deterministische Identität.

import { makeRng, rngRange, rngInt, hashString } from './rng.js';
import { PAINT_SURFACE_PX_PER_M, PAINT_MAX_TEXTURE } from './constants.js';

/**
 * Waggontypen. Maße in Metern, Bezugspunkt ist die Mitte der Bodenplatte
 * über Schienenoberkante (deckHeight).
 *
 * ride: wo man mitfahren kann
 *   - 'top'    : oben drauf (offener Hopper: auf der Kohle / am Rand)
 *   - 'inside' : im Innenraum (Boxcar mit offener Tür)
 *   - 'porch'  : auf der Endbühne / zwischen den Waggons
 *   - 'none'   : nichts zu holen, nur Fläche zum Malen
 */
export const CAR_TYPES = {
  loco: {
    id: 'loco',
    label: 'Lok',
    length: 21.4, width: 3.1, height: 4.7,
    deckHeight: 1.28,
    mass: 190,
    ride: 'porch',
    climbSides: ['front-left', 'front-right'],
    // Malflächen: Seitenwände. Bewusst kleiner als beim Waggon — Loks werden
    // seltener bemalt und der Bereich ist gefährlicher.
    paintSurfaces: [
      { id: 'l', side: 'left',  w: 18.0, h: 2.2, cy: 2.4 },
      { id: 'r', side: 'right', w: 18.0, h: 2.2, cy: 2.4 },
    ],
    interior: null,
  },

  hopper: {
    // Offener Kohlewagen. Fährt sich schlecht bei Regen (Konzept §2.1: nicht der
    // Zug ist falsch, die Paarung ist falsch). Seitenwände und Mulde geben
    // etwas mehr Windschatten als der Flachwagen — spürbar, nicht rettend.
    id: 'hopper',
    label: 'Offener Kohlewagen',
    length: 13.8, width: 3.05, height: 3.5,
    deckHeight: 1.14,
    mass: 92,
    ride: 'top',
    exposure: 0.92,
    climbSides: ['left', 'right', 'front', 'back'],
    paintSurfaces: [
      { id: 'l', side: 'left',  w: 12.6, h: 2.0, cy: 2.2 },
      { id: 'r', side: 'right', w: 12.6, h: 2.0, cy: 2.2 },
    ],
    interior: null,
    load: 'coal',
  },

  boxcar: {
    // Geschlossener Waggon. Mit offener Tür der beste Platz im Zug: trocken,
    // windstill, man sieht raus.
    id: 'boxcar',
    label: 'Geschlossener Waggon',
    length: 16.2, width: 3.15, height: 4.1,
    deckHeight: 1.18,
    mass: 78,
    ride: 'inside',
    exposure: 0.25,
    climbSides: ['left', 'right'],
    paintSurfaces: [
      // Die klassische Freight-Graffiti-Fläche.
      { id: 'l', side: 'left',  w: 15.0, h: 2.7, cy: 2.5 },
      { id: 'r', side: 'right', w: 15.0, h: 2.7, cy: 2.5 },
      { id: 'e', side: 'back',  w: 2.9,  h: 2.7, cy: 2.5 },
    ],
    interior: {
      // Innenraum als Box relativ zur Waggonmitte.
      length: 14.8, width: 2.7, height: 2.6,
      floorY: 1.22,
      doors: [
        { side: 'left',  offset: 0.0, width: 2.4 },
        { side: 'right', offset: 0.0, width: 2.4 },
      ],
      // Innenwände sind auch bemalbar — Konzept §6.2: abgewandte Seiten,
      // Zwischenbereiche, das erzeugt das Suchverb.
      paintSurfaces: [
        { id: 'il', side: 'inner-left',  w: 13.8, h: 2.3, cy: 2.4 },
        { id: 'ir', side: 'inner-right', w: 13.8, h: 2.3, cy: 2.4 },
      ],
    },
  },

  tank: {
    // Kesselwagen: ganz zu, nichts zu holen, aber der Rahmen trägt.
    id: 'tank',
    label: 'Kesselwagen',
    length: 14.6, width: 3.0, height: 3.9,
    deckHeight: 1.16,
    mass: 88,
    ride: 'porch',
    exposure: 0.85,
    climbSides: ['front', 'back'],
    paintSurfaces: [
      { id: 'l', side: 'left',  w: 11.0, h: 1.9, cy: 2.5, curved: true },
      { id: 'r', side: 'right', w: 11.0, h: 1.9, cy: 2.5, curved: true },
    ],
    interior: null,
  },

  gondola: {
    // Offener Kastenwagen: man kann rein, aber oben offen. Regen kommt an,
    // Wind weniger. Der interessante Mittelweg.
    id: 'gondola',
    label: 'Offener Kastenwagen',
    length: 15.0, width: 3.1, height: 3.2,
    deckHeight: 1.14,
    mass: 74,
    ride: 'inside',
    exposure: 0.6,
    climbSides: ['left', 'right', 'front', 'back'],
    paintSurfaces: [
      { id: 'l', side: 'left',  w: 13.8, h: 1.8, cy: 2.1 },
      { id: 'r', side: 'right', w: 13.8, h: 1.8, cy: 2.1 },
    ],
    interior: {
      length: 13.6, width: 2.7, height: 1.9,
      floorY: 1.18,
      openTop: true,
      doors: [],
      paintSurfaces: [
        { id: 'il', side: 'inner-left',  w: 13.0, h: 1.6, cy: 2.0 },
        { id: 'ir', side: 'inner-right', w: 13.0, h: 1.6, cy: 2.0 },
      ],
    },
  },

  flat: {
    // Flachwagen: der nasse, kalte, ehrliche Platz (Konzept §2.1, Beispiel 1).
    // exposure 1.0 — messbar schlimmer als Hopper (0.92) und Gondola (0.6).
    id: 'flat',
    label: 'Flachwagen',
    length: 18.0, width: 3.1, height: 1.4,
    deckHeight: 1.26,
    mass: 62,
    ride: 'top',
    exposure: 1.0,
    climbSides: ['left', 'right', 'front', 'back'],
    paintSurfaces: [
      { id: 'l', side: 'left',  w: 16.0, h: 0.85, cy: 0.9 },
      { id: 'r', side: 'right', w: 16.0, h: 0.85, cy: 0.9 },
    ],
    interior: null,
  },
};

/** Waggonnummern im US-Reporting-Mark-Stil: vier Buchstaben + Ziffern. */
const REPORTING_MARKS = ['CFRX', 'MJLR', 'BNSF', 'ATSF', 'SPCX', 'DRGW', 'GNWR', 'WPRR'];

/** Sichtbare Beschädigungen, damit Waggons wiedererkennbar sind. */
const DAMAGE_TAGS = [
  'dent-left', 'dent-right', 'rust-streak', 'patched-panel', 'bent-ladder',
  'missing-plate', 'faded-paint', 'burn-mark', 'graffiti-ghost', 'oil-stain',
];

/** Grundfarben — reduzierte Palette (Konzept §12.4). */
const CAR_BASE_COLORS = [
  '#6b4a3a', '#7a4640', '#4a5a52', '#5b5f66', '#8a6a45',
  '#3f4a58', '#6d5b3f', '#55403a', '#4d4d4d', '#7d6b52',
];

/**
 * Erzeugt die Identität eines Waggons deterministisch aus seiner ID.
 * Dieselbe carId liefert überall dieselbe Nummer, Farbe und Beschädigung.
 */
export function buildCarIdentity(carId, typeId) {
  const rng = makeRng(hashString(`car:${carId}`));
  const mark = REPORTING_MARKS[rngInt(rng, 0, REPORTING_MARKS.length - 1)];
  const number = String(rngInt(rng, 10000, 99999));
  const damageCount = rngInt(rng, 0, 2);
  const damage = [];
  for (let i = 0; i < damageCount; i++) {
    const d = DAMAGE_TAGS[rngInt(rng, 0, DAMAGE_TAGS.length - 1)];
    if (!damage.includes(d)) damage.push(d);
  }
  return {
    reportingMark: mark,
    number,
    stencil: `${mark} ${number}`,
    baseColor: CAR_BASE_COLORS[rngInt(rng, 0, CAR_BASE_COLORS.length - 1)],
    rustLevel: rngRange(rng, 0.05, 0.85),
    damage,
    // Kleine Zufallsvariation der Aufbauhöhe, damit die Silhouette nicht
    // gleichförmig wird.
    heightJitter: rngRange(rng, -0.06, 0.06),
    typeId,
  };
}

/**
 * Baut die eine Zugkomposition der Season.
 * Vorgabe: 10 Waggons, gemischt — Kohle, ganz zu, mit Innenraum.
 *
 * Feste, handgewählte Reihenfolge statt Zufall: so ist der Zug ein Charakter,
 * kein Würfelergebnis, und die Mischung ist garantiert interessant
 * (guter Platz vorne, schlechter hinten, Suchverb dazwischen).
 */
export const CONSIST_PLAN = [
  { id: 'loco-1',  type: 'loco'    },
  { id: 'car-01',  type: 'hopper'  }, // Kohle
  { id: 'car-02',  type: 'boxcar'  }, // Innenraum, offene Tür
  { id: 'car-03',  type: 'hopper'  }, // Kohle
  { id: 'car-04',  type: 'tank'    }, // ganz zu
  { id: 'car-05',  type: 'gondola' }, // Innenraum, oben offen
  { id: 'car-06',  type: 'hopper'  }, // Kohle
  { id: 'car-07',  type: 'flat'    }, // exponiert
  { id: 'car-08',  type: 'boxcar'  }, // Innenraum
  { id: 'car-09',  type: 'tank'    }, // ganz zu
  { id: 'car-10',  type: 'hopper'  }, // Kohle
];

/**
 * Durchfahrender Güterzug (schedule.TRAIN_THROUGH): kürzer, andere Mischung.
 *
 * Warum anders: Konzept §6.2 — Wiedererkennung. Der Spieler soll am Aussehen
 * sehen, welcher Zug kommt (Kessel/Flach-lastig vs. Kohle-Pendler), ohne UI.
 * IDs mit `thr-`-Präfix: sonst kollidieren Graffiti-surfaceIds mit dem Local.
 */
export const THROUGH_CONSIST_PLAN = [
  { id: 'thr-loco-1', type: 'loco'    },
  { id: 'thr-01',     type: 'tank'    }, // Kessel-Kette: Fernfracht-Silhouette
  { id: 'thr-02',     type: 'tank'    },
  { id: 'thr-03',     type: 'flat'    },
  { id: 'thr-04',     type: 'gondola' },
  { id: 'thr-05',     type: 'tank'    },
  { id: 'thr-06',     type: 'flat'    },
  { id: 'thr-07',     type: 'tank'    },
  { id: 'thr-08',     type: 'boxcar'  }, // ein geschlossener Wagen als Kontrast
];

export const COUPLER_GAP = 1.05; // m zwischen zwei Waggonkästen

/**
 * Vollständige Zugbeschreibung mit Offsets entlang des Zuges.
 * offset = Abstand der Waggonmitte hinter der Zugspitze (positiv nach hinten).
 */
export function buildConsist(plan = CONSIST_PLAN) {
  const cars = [];
  let cursor = 0;
  for (let i = 0; i < plan.length; i++) {
    const entry = plan[i];
    const type = CAR_TYPES[entry.type];
    if (!type) throw new Error(`Unbekannter Waggontyp: ${entry.type}`);
    const half = type.length / 2;
    cursor += half;
    const identity = buildCarIdentity(entry.id, entry.type);
    cars.push({
      id: entry.id,
      index: i,
      typeId: entry.type,
      type,
      offset: cursor, // Mitte hinter der Spitze
      length: type.length,
      identity,
      // Manche Boxcar-Türen sind zu — das erzeugt das Absuchen.
      doorOpen: type.ride === 'inside'
        ? (hashString(`door:${entry.id}`) % 100) < 70
        : false,
      // Kohlemenge variiert: manche randvoll, manche halb leer.
      loadLevel: type.load === 'coal'
        ? 0.35 + (hashString(`load:${entry.id}`) % 1000) / 1000 * 0.6
        : 0,
      paintSurfaces: enumeratePaintSurfaces(entry.id, type),
    });
    cursor += half + COUPLER_GAP;
  }
  const totalLength = cursor - COUPLER_GAP;
  return { cars, totalLength, carCount: cars.length };
}

/**
 * Alle bemalbaren Flächen eines Waggons mit stabilen globalen IDs.
 * Die surfaceId ist der Persistenzschlüssel für Graffiti.
 */
export function enumeratePaintSurfaces(carId, type) {
  const out = [];
  const add = (surface, location) => {
    const pxW = Math.min(PAINT_MAX_TEXTURE, Math.round(surface.w * PAINT_SURFACE_PX_PER_M));
    const pxH = Math.min(PAINT_MAX_TEXTURE, Math.round(surface.h * PAINT_SURFACE_PX_PER_M));
    out.push({
      surfaceId: `${carId}:${surface.id}`,
      carId,
      location, // 'exterior' | 'interior'
      side: surface.side,
      widthM: surface.w,
      heightM: surface.h,
      centerY: surface.cy,
      curved: !!surface.curved,
      pxW, pxH,
    });
  };
  for (const s of type.paintSurfaces ?? []) add(s, 'exterior');
  for (const s of type.interior?.paintSurfaces ?? []) add(s, 'interior');
  return out;
}

/** Statische Malflächen in der Welt (Konzept §6.1: Wassertürme, Mauern). */
export const STATIC_PAINT_SURFACES = [
  {
    surfaceId: 'wall:south-yard',
    kind: 'wall',
    s: 300, lateral: 13.0, facing: -1,
    widthM: 14.0, heightM: 3.2, centerY: 1.9,
    note: 'Hofmauer Cinder Flats',
  },
  {
    surfaceId: 'wall:south-bunker',
    kind: 'wall',
    s: 480, lateral: -12.0, facing: 1,
    widthM: 9.0, heightM: 2.6, centerY: 1.6,
    note: 'Kohlebunker-Rückseite',
  },
  {
    surfaceId: 'tower:north-water',
    kind: 'tower',
    s: 5080, lateral: -11.0, facing: 1,
    widthM: 7.5, heightM: 3.4, centerY: 8.4,
    note: 'Wasserturm am Scheitelpunkt — hoch, gute Funkreichweite (§9)',
  },
  {
    surfaceId: 'bridge:pier-south',
    kind: 'pier',
    s: 5570, lateral: 9.0, facing: -1,
    widthM: 5.0, heightM: 6.0, centerY: 4.0,
    note: 'Brückenpfeiler',
  },
  {
    surfaceId: 'wall:north-relay',
    kind: 'relay',
    s: 6880, lateral: 10.5, facing: -1,
    widthM: 6.0, heightM: 2.4, centerY: 1.6,
    note: 'Relaishäuschen Mott Junction',
  },
  {
    surfaceId: 'wall:north-yard',
    kind: 'wall',
    s: 7040, lateral: -11.5, facing: 1,
    widthM: 12.0, heightM: 3.0, centerY: 1.8,
    note: 'Hofmauer Mott Junction',
  },
];

export function allStaticSurfaceIds() {
  return STATIC_PAINT_SURFACES.map((s) => s.surfaceId);
}

/** Liefert eine Fläche (Waggon oder statisch) per ID, oder null. */
export function findSurface(surfaceId, consist) {
  const stat = STATIC_PAINT_SURFACES.find((s) => s.surfaceId === surfaceId);
  if (stat) return { ...stat, location: 'world' };
  if (!consist) return null;
  for (const car of consist.cars) {
    const s = car.paintSurfaces.find((p) => p.surfaceId === surfaceId);
    if (s) return s;
  }
  return null;
}
